import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"
import { EASE_OUT, prefersReducedMotion } from "@/lib/motion"
import { afterSettle } from "@/Hooks/useSettledValue"

// How many times the tab has changed since the tabs appeared, so a panel
// fades in when the user moves to it but not on the page's first paint (or a
// deep link). `animated={false}` (the public viewer, which has its own
// indicator motion) keeps it at 0.
const TabsChangeContext = React.createContext(0)

const Tabs = React.forwardRef(({ value, onValueChange, animated = true, ...props }, ref) => {
  const [changes, setChanges] = React.useState(0)
  const [shownValue, setShownValue] = React.useState(value)

  // A controlled value can change without the strip (a button elsewhere on
  // the page opening a tab); that is a change too.
  if (value !== undefined && value !== shownValue) {
    setShownValue(value)
    setChanges((count) => count + 1)
  }

  return (
    <TabsChangeContext.Provider value={animated ? changes : 0}>
      <TabsPrimitive.Root
        ref={ref}
        value={value}
        onValueChange={(next) => {
          if (value === undefined) setChanges((count) => count + 1)
          onValueChange?.(next)
        }}
        {...props} />
    </TabsChangeContext.Provider>
  )
})
Tabs.displayName = TabsPrimitive.Root.displayName

// `underline` is the page-level tab strip: a hairline baseline with a 1.5px
// indicator under the active tab, scrolling sideways on a narrow screen rather
// than swapping itself for a <Select>. The default look is unchanged for every
// existing caller.
//
// The strip scrolls, and a scroll container clips on both axes, so nothing may
// reach outside it: the baseline is an inset shadow painted under the tabs,
// the indicator a pseudo-element inside each tab, and the focus ring is inset.
// A tab pulled onto a real border with a negative margin gets cut in half
// against it.
//
// `indicator` (underline only) swaps the per-tab underline for one bar that
// slides from the tab left to the tab chosen. It lives inside the strip, so
// it scrolls with it.
const TabsVariantContext = React.createContext({ variant: "default", indicator: false })

// The underline's inset from its tab's edges, as `after:inset-x-1.5` draws it.
const INDICATOR_INSET_PX = 6

function SlidingIndicator({ className }) {
  const bar = React.useRef(null)
  const [box, setBox] = React.useState(null)
  const [placed, setPlaced] = React.useState(false)

  // The strip is the bar's parent. (A ref handed down from the strip is not
  // attached yet when this effect runs: children's layout effects come first.)
  React.useLayoutEffect(() => {
    const list = bar.current?.parentElement
    if (!list) return undefined
    const place = () => {
      const active = list.querySelector('[role="tab"][data-state="active"]')
      setBox(active
        ? { left: active.offsetLeft + INDICATOR_INSET_PX, width: Math.max(active.offsetWidth - 2 * INDICATOR_INSET_PX, 0) }
        : null)
    }
    place()
    const states = new MutationObserver(place)
    states.observe(list, { subtree: true, attributes: true, attributeFilter: ["data-state"] })
    // A tab that grows (a count arriving) moves the ones after it.
    const sizes = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place)
    list.querySelectorAll('[role="tab"]').forEach((tab) => sizes?.observe(tab))
    return () => {
      states.disconnect()
      sizes?.disconnect()
    }
  }, [])

  // The first placement is where the bar starts, not a move: it glides only
  // from the frame after.
  React.useEffect(() => {
    if (!box || placed) return undefined
    const frame = requestAnimationFrame(() => setPlaced(true))
    return () => cancelAnimationFrame(frame)
  }, [box, placed])

  return (
    <span
      ref={bar}
      aria-hidden="true"
      hidden={!box}
      className={cn(
        "pointer-events-none absolute bottom-0 left-0 h-[1.5px] w-px origin-left rounded-full bg-zinc-950",
        className,
        placed && "transition-transform duration-200 ease-mk-in-out"
      )}
      style={box ? { transform: `translateX(${box.left}px) scaleX(${box.width})` } : undefined} />
  )
}

// `indicatorClassName` recolours the sliding bar (the Lab's Bulk edit tab
// carries the one accent: orange where something writes).
const TabsList = React.forwardRef(({ className, variant = "default", indicator = false, indicatorClassName, children, ...props }, ref) => {
  const sliding = variant === "underline" && indicator
  const context = React.useMemo(() => ({ variant, indicator: sliding }), [variant, sliding])

  return (
    <TabsVariantContext.Provider value={context}>
      <TabsPrimitive.List
        ref={ref}
        className={cn(
          variant === "underline"
            ? "flex w-full items-center gap-3 overflow-x-auto shadow-[inset_0_-1px_0_theme(colors.zinc.200)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            : "inline-flex h-11 items-center justify-center rounded-lg text-muted-foreground",
          sliding && "relative",
          className
        )}
        {...props}>
        {children}
        {sliding && <SlidingIndicator className={indicatorClassName} />}
      </TabsPrimitive.List>
    </TabsVariantContext.Provider>
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => {
  const { variant, indicator } = React.useContext(TabsVariantContext)

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        variant === "underline"
          ? "relative inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-1.5 text-[13px] font-medium text-zinc-500 transition-colors after:absolute after:inset-x-1.5 after:bottom-0 after:h-[1.5px] after:rounded-full after:bg-transparent after:transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900/15 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-zinc-950"
          : "inline-flex text-muted-foreground items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-base ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50  data-[state=active]:text-foreground data-[state=active]:font-medium",
        variant === "underline" && !indicator && "data-[state=active]:after:bg-zinc-950",
        className
      )}
      {...props} />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

// A panel the user has just moved to fades in, 2px up, over 150ms: tabs are
// switched many times a day, so it is the least that still reads as the
// panel changing rather than the page flickering. It holds its first frame
// until two frames on (afterSettle), since a heavy panel (the ontology
// editor, the composer) would otherwise spend the fade while mounting.
const TabsContent = React.forwardRef(({ className, ...props }, ref) => {
  const changes = React.useContext(TabsChangeContext)
  const own = React.useRef(null)
  const setRef = React.useCallback((node) => {
    own.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])

  React.useLayoutEffect(() => {
    const panel = own.current
    if (changes === 0 || !panel || panel.dataset.state !== "active" || typeof panel.animate !== "function" || prefersReducedMotion()) {
      return undefined
    }
    const fade = panel.animate(
      [{ opacity: 0, transform: "translateY(2px)" }, { opacity: 1, transform: "none" }],
      { duration: 150, easing: EASE_OUT, fill: "backwards" }
    )
    fade.pause()
    const cancel = afterSettle(() => fade.play())
    return () => {
      cancel()
      fade.finish()
    }
  }, [changes])

  return (
    <TabsPrimitive.Content
      ref={setRef}
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      )}
      {...props} />
  )
})
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
